/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {importCarbData} from '@ion/core/src/services/Connect';

export interface UseCarbImportParams {
  requestType: string;
  entityIdentifierType: string,
  accountId: number,
  taskNickName: string,
  entityIdentifierS3Path: string
};

export type UseCarbImportResult = {
  importCarb: (params: UseCarbImportParams, callbacks) => void;
  isProcessing: boolean;
}

/**
 * Hook for calling the Import CARB data api for uploading CARB data.
 *
 * ```typescript
 *
 *  import {useCarbImport} from '@ion/packages/query/src/hooks';
 *
 *  const {process, isProcessing} = useCarbImport({
 *  entityIdentifierType,
 *  accountId,
 *  requestType,
 *  taskNickName,
 *  entityIdentifierS3Path
 *  });
 *
 * ```
 */
export const useCarbImport = (): UseCarbImportResult => {
  const {mutate, isLoading} = useMutation(async({
    entityIdentifierType,
    accountId,
    requestType,
    taskNickName,
    entityIdentifierS3Path
  }: UseCarbImportParams) => {
    const operation: UseCarbImportParams = {
      entityIdentifierType,
      accountId,
      requestType,
      taskNickName,
      entityIdentifierS3Path
    };
    return await importCarbData(operation);
  });

  return {
    importCarb   : mutate,
    isProcessing : isLoading
  };
};

export default useCarbImport;
